import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Compass, X, ArrowRight, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useAuth, API } from '../App';

export const DEALROOM_TOUR_EVENT = 'idfc-start-dealroom-tour';
export const startDealRoomTour = () => window.dispatchEvent(new CustomEvent(DEALROOM_TOUR_EVENT));
const DONE_KEY = 'gcfe_dealroom_tour_done';

// English only — IDFC official language. {id} is replaced with a demo project id at runtime.
const buildSteps = (projectId) => [
  {
    title: 'Your first deal room in 5 minutes',
    body: "We'll walk you through the co-financing journey on demo data: discover a project, read its AI intelligence, open a deal room and track it on the pipeline. Takes about 5 minutes.",
    path: '/dashboard',
  },
  {
    title: 'Step 1 · The shared pipeline',
    body: 'This dashboard is the shared network pipeline. Every member institution publishes its projects here with key indicators — bankability, funding gap, sector and country. Use the search to filter instantly.',
    path: '/dashboard',
    target: '[data-testid="search-input"]',
  },
  {
    title: 'Step 2 · Open a demo project',
    body: 'Let\'s open a demo project to see its standardized sheet. We\'ll take you there now.',
    path: projectId ? `/projects/${projectId}` : '/dashboard',
  },
  {
    title: 'Step 3 · AI intelligence & matching',
    body: 'Each project sheet gives you an AI summary, key risks and a co-financing score per member institution. Scores are a transparent starting point — never a verdict. The "Why?" button generates an AI rationale with talking points.',
    path: projectId ? `/projects/${projectId}` : '/dashboard',
    target: '[data-testid="tab-summary"]',
  },
  {
    title: 'Step 4 · Enter the deal room',
    body: 'Click "Co-Finance / Participate" to join the deal. That unlocks the isolated deal room: secure document sharing, inter-institution messaging and internal notes visible only to your institution.',
    path: projectId ? `/projects/${projectId}` : '/dashboard',
    target: '[data-testid="participate-button"]',
  },
  {
    title: 'Step 5 · Structure the deal',
    body: 'In the deal room you build the term sheet, add commitments to the syndication table and set conditions precedent. Coverage and the residual funding gap update automatically as commitments firm up.',
    path: projectId ? `/projects/${projectId}` : '/dashboard',
    target: '[data-testid="tab-structure"]',
  },
  {
    title: 'Step 6 · Track it on the pipeline',
    body: 'Every deal moves across the 5-column Kanban pipeline, from prospecting to closing. Drag and drop a card to update its stage — the whole network stays in sync.',
    path: '/pipeline',
  },
  {
    title: "You're all set!",
    body: "That's the 5-minute journey. Explore freely on the demo data, and use the green Feedback button (bottom-right) to send us your experience anytime.",
    path: '/dashboard',
    last: true,
  },
];

const Spotlight = ({ selector, step }) => {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    if (!selector) { setRect(null); return undefined; }
    let raf;
    let tries = 0;
    const measure = () => {
      const el = document.querySelector(selector);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else if (tries < 20) {
        tries += 1;
        raf = setTimeout(measure, 200);
      } else {
        setRect(null);
      }
    };
    const start = setTimeout(measure, 500);
    const onScroll = () => {
      const el = document.querySelector(selector);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      clearTimeout(start);
      if (raf) clearTimeout(raf);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selector, step]);

  if (!rect) return null;
  const pad = 8;
  return (
    <div
      className="fixed z-[9000] pointer-events-none rounded-lg"
      style={{
        top: rect.top - pad,
        left: rect.left - pad,
        width: rect.width + pad * 2,
        height: rect.height + pad * 2,
        boxShadow: '0 0 0 3px #45B08F, 0 0 0 9999px rgba(3,12,20,0.55)',
        transition: 'all 0.25s ease',
      }}
      data-testid="dealroom-spotlight"
    />
  );
};

const DealRoomTour = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [run, setRun] = useState(false);
  const [index, setIndex] = useState(0);
  const [steps, setSteps] = useState(buildSteps(null));
  const startedRef = useRef(false);
  const autoTimerRef = useRef(null);

  const loadDemoProject = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects`);
      const list = res.data.projects || res.data || [];
      const demo = list.find((p) => p.is_demo) || list[0];
      return demo?.project_id || null;
    } catch {
      return null;
    }
  }, []);

  const begin = useCallback(async () => {
    startedRef.current = true;
    if (autoTimerRef.current) { clearInterval(autoTimerRef.current); autoTimerRef.current = null; }
    const pid = await loadDemoProject();
    setSteps(buildSteps(pid));
    setIndex(0);
    setRun(true);
    navigate('/dashboard');
  }, [loadDemoProject, navigate]);

  // Manual relaunch (Dashboard / Profile buttons)
  useEffect(() => {
    const handler = () => begin();
    window.addEventListener(DEALROOM_TOUR_EVENT, handler);
    return () => window.removeEventListener(DEALROOM_TOUR_EVENT, handler);
  }, [begin]);

  // Auto-start once, only in demo mode, and only after the standard onboarding welcome is gone.
  useEffect(() => {
    if (!user || startedRef.current) return undefined;
    if (localStorage.getItem(DONE_KEY)) return undefined;
    let cancelled = false;
    let interval;
    axios.get(`${API}/maintenance/status`).then((res) => {
      if (cancelled || !res.data?.demo_mode) return;
      const tryStart = () => {
        if (cancelled || startedRef.current) return;
        // Never yank the user off a page they navigated to (project detail, submit, edit…).
        const path = window.location.pathname;
        if (path !== '/briefing' && path !== '/dashboard') return;
        const welcome = document.querySelector('[data-testid="onboarding-welcome-modal"]');
        const joyride = document.querySelector('[data-testid="tour-tooltip"]');
        if (!welcome && !joyride) {
          startedRef.current = true;
          if (interval) clearInterval(interval);
          begin();
        }
      };
      interval = setInterval(tryStart, 2500);
      autoTimerRef.current = interval;
      setTimeout(tryStart, 3000);
    }).catch(() => {});
    return () => { cancelled = true; if (interval) clearInterval(interval); };
  }, [user, begin]);

  const finish = useCallback(() => {
    setRun(false);
    localStorage.setItem(DONE_KEY, '1');
  }, []);

  // Keep the route in sync with the current step (avoids click-timing lag).
  useEffect(() => {
    if (!run) return;
    const step = steps[index];
    if (step?.path && window.location.pathname !== step.path) {
      navigate(step.path);
    }
  }, [run, index, steps, navigate]);

  const goTo = useCallback((next) => {
    if (next < 0 || next >= steps.length) return;
    setIndex(next);
  }, [steps]);

  if (!user || !run) return null;
  if (!(user.nda_accepted_at && user.nda_version === user.current_nda_version)) return null;
  const step = steps[index];
  if (!step) return null;

  return (
    <>
      <Spotlight selector={step.target} step={index} />
      <div
        className="fixed left-5 z-[9001] w-[340px] max-w-[calc(100vw-2.5rem)] bg-[#184252] border border-[#45B08F]/40 rounded-xl shadow-2xl shadow-black/50 p-5"
        style={{ bottom: 44 }}
        data-testid="dealroom-tour-card"
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#45B08F]/15 flex items-center justify-center">
              {step.last ? <CheckCircle2 className="w-4 h-4 text-[#45B08F]" /> : <Compass className="w-4 h-4 text-[#45B08F]" />}
            </div>
            <span className="text-[11px] font-mono tracking-wider text-slate-500">{index + 1} / {steps.length}</span>
          </div>
          <button onClick={finish} className="text-slate-500 hover:text-white transition-colors" data-testid="dealroom-tour-close" title="Close tour">
            <X className="w-4 h-4" />
          </button>
        </div>

        <h3 className="text-white font-semibold text-[15px] mb-1.5" data-testid="dealroom-tour-title">{step.title}</h3>
        <p className="text-slate-300 text-[13px] leading-relaxed mb-4">{step.body}</p>

        {/* Progress dots */}
        <div className="flex items-center gap-1.5 mb-4">
          {steps.map((_, i) => (
            <span key={i} className={`h-1 rounded-full transition-all ${i === index ? 'w-5 bg-[#45B08F]' : 'w-1.5 bg-[#285D71]'}`} />
          ))}
        </div>

        <div className="flex items-center justify-between">
          <button
            onClick={finish}
            className="text-[12px] text-slate-500 hover:text-slate-300 transition-colors"
            data-testid="dealroom-tour-skip"
          >
            Skip
          </button>
          <div className="flex items-center gap-2">
            {index > 0 && (
              <button
                onClick={() => goTo(index - 1)}
                className="flex items-center gap-1 text-[13px] text-slate-300 hover:text-white px-2 py-1.5 transition-colors"
                data-testid="dealroom-tour-back"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </button>
            )}
            {step.last ? (
              <button
                onClick={finish}
                className="flex items-center gap-1.5 text-[13px] font-medium text-white bg-[#45B08F] hover:bg-[#3A9679] rounded-lg px-4 py-2 transition-colors"
                data-testid="dealroom-tour-done"
              >
                Done <CheckCircle2 className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button
                onClick={() => goTo(index + 1)}
                className="flex items-center gap-1.5 text-[13px] font-medium text-white bg-[#45B08F] hover:bg-[#3A9679] rounded-lg px-4 py-2 transition-colors"
                data-testid="dealroom-tour-next"
              >
                Next <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DealRoomTour;
